import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  Table,
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useSessionStore } from "../../stores/sessionStore";
import { api } from "../../config/middleware";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../../stores/pagingStore";
import {
  API_GETPRODUCTS,
  API_ADDPRODUCT,
  API_EDITPRODUCT,
  API_DELPRODUCT,
} from "../../config/api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import LoadingSpinner from "components/LoadingSpinner";
import { useLocation } from "react-router-dom";

const TABLE_KEY = "products";

const ProductCustomer = () => {
  const MySwal = withReactContent(Swal);

  const location = useLocation();
  const customer = location.state?.customer;

  const orgId = useSessionStore((s) => s.profile?.orgId);
  const cmCode = useSessionStore((s) => s.profile?.cmCode);

  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY);

  // ✅ responsive: จอเล็กใช้ table เดิม
  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ===== data/paging state =====
  const [total, setTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState([]); // server-paged: แถวของหน้าปัจจุบัน
  const [allRows, setAllRows] = useState([]); // client-paged: ดึงทั้งหมด
  const [clientPaged, setClientPaged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // search
  const [keyword, setKeyword] = useState("");
  const serverModeRef = useRef(false); // เดิมเป็น server-paged ไหม
  const reqIdRef = useRef(0);

  // modal & form (เพิ่ม/แก้ไข)
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("add"); // add | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", price: 0, weight: 0 });
  const [submitting, setSubmitting] = useState(false);

  const toggle = () => setIsOpen((v) => !v);
  const onChange = (e) => {
    const { name, value, type } = e.target;
    setForm((s) => ({
      ...s,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  // ===== helpers =====
  const getId = (it, i) =>
    it.pdId ?? it.ID ?? `${it.pdCode ?? it.code ?? "row"}-${i}`;

  const dedupeBy = (arr, idFn) => {
    const m = new Map();
    arr.forEach((x, i) => m.set(idFn(x, i), x));
    return [...m.values()];
  };

  const pickTotal = (d) =>
    Number(
      d?.total ?? d?.count ?? d?.pagination?.total ?? d?.meta?.total ?? NaN,
    );

  const norm = (s) => (s || "").toString().toLowerCase().trim();

  // ===== base rows + filter =====
  const baseRows = clientPaged ? allRows : rowsPage;
  const filteredRows = useMemo(() => {
    const k = norm(keyword);
    if (!k) return baseRows;
    return baseRows.filter(
      (r) =>
        norm(r.pdCode ?? r.code ?? "").includes(k) ||
        norm(r.pdName ?? r.name ?? "").includes(k),
    );
  }, [baseRows, keyword]);

  const filteredTotal = filteredRows.length;
  const filteredPageCount = Math.max(1, Math.ceil(filteredTotal / pageSize));

  const displayRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  // ===== โหลดหน้าแรก =====
  const fetchFirstPage = async () => {
    if (!orgId) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    setClientPaged(false);
    serverModeRef.current = false;
    setRowsPage([]);
    setAllRows([]);
    setTotal(0);
    setPage(TABLE_KEY, 1);

    try {
      const res = await api.get(API_GETPRODUCTS, {
        params: { ORGID: orgId, CMCODE: customer.cmCode },
      });
      if (myReq !== reqIdRef.current) return;

      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const dedup = dedupeBy(raw, getId);
      const totalFromApi = pickTotal(res.data);
      const apiLooksPaged =
        Number.isFinite(totalFromApi) && dedup.length <= pageSize;

      if (apiLooksPaged) {
        // server-paged
        setClientPaged(false);
        serverModeRef.current = true;
        setRowsPage(dedup);
        setTotal(totalFromApi);
      } else {
        // client-paged
        serverModeRef.current = false;
        setClientPaged(true);
        const all = dedupeBy(raw, getId);
        setAllRows(all);
        setTotal(all.length);
      }
    } catch (e) {
      console.error("fetchFirstPage error:", e);
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  // ===== โหลดหน้า N (server-paged เท่านั้น) =====
  const fetchServerPage = async (nextPage) => {
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    try {
      const res = await api.get(API_GETPRODUCTS, {
        params: {
          ORGID: orgId,
          CMCODE: cmCode,
          PAGE: nextPage,
          LIMIT: pageSize,
        },
      });
      if (myReq !== reqIdRef.current) return;
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const dedup = dedupeBy(raw, getId);
      setRowsPage(dedup);
    } catch (e) {
      console.error("fetchServerPage error:", e);
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  // ===== ดึงทั้งหมดเพื่อค้นหาในโหมด server =====
  const fetchAllForSearch = async () => {
    if (!orgId) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    try {
      const first = await api.get(API_GETPRODUCTS, {
        params: { ORGID: orgId, CMCODE: cmCode, PAGE: 1, LIMIT: pageSize },
      });
      if (myReq !== reqIdRef.current) return;

      const firstRows = Array.isArray(first.data?.data) ? first.data.data : [];
      const totalAll = pickTotal(first.data) || firstRows.length;
      const pages = Math.max(1, Math.ceil(totalAll / pageSize));
      let all = dedupeBy(firstRows, getId);

      for (let p = 2; p <= pages; p++) {
        const r = await api.get(API_GETPRODUCTS, {
          params: { ORGID: orgId, CMCODE: cmCode, PAGE: p, LIMIT: pageSize },
        });
        if (myReq !== reqIdRef.current) return;
        const part = Array.isArray(r.data?.data) ? r.data.data : [];
        all = dedupeBy([...all, ...part], getId);
      }

      setClientPaged(true);
      serverModeRef.current = true;
      setAllRows(all);
      setTotal(all.length);
      setPage(TABLE_KEY, 1);
    } catch (e) {
      console.error("fetchAllForSearch error:", e);
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  // ===== lifecycle =====
  useEffect(() => {
    fetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (!clientPaged) fetchServerPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!orgId) return;
    setKeyword("");
    fetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useEffect(() => {
    setPage(TABLE_KEY, 1);
    if (keyword && !clientPaged) fetchAllForSearch();
    if (!keyword && serverModeRef.current && clientPaged) {
      fetchFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  // ===== open modal =====
  const openAddModal = () => {
    setMode("add");
    setEditingId(null);
    setForm({ code: "", name: "", price: 0, weight: 0 });
    setIsOpen(true);
  };

  const openEditModal = (row) => {
    setMode("edit");
    setEditingId(row.pdId ?? row.ID ?? null);
    setForm({
      code: row.pdCode ?? row.code ?? "",
      name: row.pdName ?? row.name ?? "",
      price: Number(row.pdPrice ?? row.price ?? 0),
      weight: Number(row.pdWeight ?? row.weight ?? 0).toFixed(2),
    });
    setIsOpen(true);
  };

  const to2 = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
  };

  // ===== CRUD =====
  const addProduct = async () => {
    const payload = {
      ORGID: orgId,
      CMCODE: customer.cmCode,
      NAME: String(form.name || "").trim(),
      PRICE: Number(form.price || 0),
      WEIGHT: to2(form.weight),
    };
    const res = await api.post(API_ADDPRODUCT, payload);
    const ok = (res?.data?.statustext || res?.data?.status || "")
      .toString()
      .toUpperCase();
    if (ok !== "SUCCESS" && ok !== "OK")
      throw new Error(res?.data?.message || "เพิ่มสินค้าไม่สำเร็จ");

    setPage(TABLE_KEY, 1);
  };

  const editProduct = async () => {
    const payload = {
      ORGID: orgId,
      CMCODE: customer.cmCode,
      PDID: editingId,
      PDCODE: String(form.code || "").trim(),
      NAME: String(form.name || "").trim(),
      PRICE: Number(form.price || 0),
      WEIGHT: to2(form.weight),
    };

    const res = await api.post(API_EDITPRODUCT, payload);

    const ok = (res?.data?.statustext || res?.data?.status || "")
      .toString()
      .toUpperCase();

    if (ok !== "SUCCESS" && ok !== "OK") {
      throw new Error(res?.data?.message || "แก้ไขสินค้าไม่สำเร็จ");
    }

    await MySwal.fire({
      icon: "success",
      title: "บันทึกสำเร็จ",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const deleteProduct = async (row) => {
    const result = await MySwal.fire({
      title: "ยืนยันการลบ?",
      text: `คุณต้องการลบสินค้า "${row.pdName ?? row.name}" ใช่หรือไม่`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await api.post(API_DELPRODUCT, {
        ORGID: orgId,
        CMCODE: customer.cmCode,
        PDID: row.pdId ?? row.ID,
        PDCODE: row.pdCode ?? row.code ?? "",
      });

      const ok = (res?.data?.statustext || res?.data?.status || "")
        .toString()
        .toUpperCase();

      if (ok !== "SUCCESS" && ok !== "OK") {
        throw new Error(res?.data?.message || "ลบสินค้าไม่สำเร็จ");
      }

      await MySwal.fire({
        icon: "success",
        title: "ลบสำเร็จ",
        timer: 1400,
        showConfirmButton: false,
      });

      const remain = displayRows.length - 1;
      if (remain <= 0 && page > 1) {
        setPage(TABLE_KEY, page - 1);
      } else {
        await fetchFirstPage();
      }
    } catch (err) {
      await MySwal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err?.message || "ลบสินค้าไม่สำเร็จ",
      });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!String(form.name || "").trim()) {
      await MySwal.fire({
        icon: "warning",
        title: "ข้อมูลไม่ครบ",
        text: "กรุณาระบุชื่อสินค้า",
      });
      return;
    }

    if (Number(form.weight || 0) <= 0) {
      await MySwal.fire({
        icon: "warning",
        title: "ข้อมูลไม่ครบ",
        text: "กรุณาระบุน้ำหนักต่อชิ้น",
      });
      return;
    }

    if (mode === "edit" && !String(form.code || "").trim()) {
      await MySwal.fire({
        icon: "warning",
        title: "ข้อมูลไม่ครบ",
        text: "กรุณาระบุรหัสสินค้า (จำเป็นสำหรับแก้ไข/ลบ)",
      });
      return;
    }

    try {
      setSubmitting(true);
      if (mode === "add") {
        await addProduct();

        await MySwal.fire({
          icon: "success",
          title: "เพิ่มสินค้าสำเร็จ",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await editProduct();
      }

      toggle();
      await fetchFirstPage();
    } catch (err) {
      await MySwal.fire({
        icon: "error",
        title: "ดำเนินการไม่สำเร็จ",
        text: err?.message || "เกิดข้อผิดพลาด",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ===== pagination buttons =====
  const buildPages = (p, max) => {
    const pages = [];
    const start = Math.max(1, p - 1);
    const end = Math.min(max, p + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (start > 1) pages.unshift(1);
    if (end < max) pages.push(max);
    return [...new Set(pages)];
  };
  const pages = buildPages(page, filteredPageCount);

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0">
                  สินค้า{" "}
                  {customer
                    ? `ของลูกค้า: ${customer.cmName} (${customer.cmCode})`
                    : ""}
                </h6>

                <Button color="success" onClick={openAddModal}>
                  เพิ่มสินค้า
                </Button>
              </CardHeader>

              <CardBody>
                {/* Toolbar: page-size + search */}
                <div className="mb-3">
                  <Row className="align-items-center g-0">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) =>
                          setPageSize(TABLE_KEY, Number(e.target.value))
                        }
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">แถว/หน้า</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="ค้นหา รหัสสินค้า/ชื่อสินค้า"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="table-modern-search w-100 w-md-auto"
                        style={{ maxWidth: 320, minWidth: 320 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ TABLE: เล็ก=เดิม, กลาง/ใหญ่=แบบรูป */}
                {isSmall ? (
                  <Table responsive hover className="table table-modern mb-0">
                    <thead className="thead-colored">
                      <tr>
                        <th className="text-start">รหัสสินค้า</th>
                        <th className="text-start">ชื่อสินค้า</th>
                        <th className="text-start">ราคาต่อชิ้น (บาท)</th>
                        <th className="text-start">
                          น้ำหนักต่อชิ้น (กิโลกรัม)
                        </th>
                        <th
                          className="text-center"
                          style={{ width: "140px", paddingRight: "50px" }}
                        >
                          จัดการ
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            กำลังโหลด...
                          </td>
                        </tr>
                      ) : displayRows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            ยังไม่มีข้อมูล
                          </td>
                        </tr>
                      ) : (
                        displayRows.map((it, i) => (
                          <tr key={getId(it, i)}>
                            <td data-th="รหัสสินค้า">{it.pdCode ?? it.code}</td>
                            <td data-th="ชื่อสินค้า">{it.pdName ?? it.name}</td>
                            <td data-th="ราคาต่อชิ้น (บาท)">
                              {Number(it.pdPrice ?? it.price ?? 0)}
                            </td>
                            <td className="text-start">
                              {Number(it.pdWeight ?? it.weight ?? 0).toFixed(2)}
                            </td>
                            <td className="text-start" data-th="จัดการ">
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  color="warning"
                                  onClick={() => openEditModal(it)}
                                >
                                  แก้ไข
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => deleteProduct(it)}
                                >
                                  ลบ
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 140 }}>
                            รหัสสินค้า
                          </th>
                          <th className="text-start" style={{ width: 140 }}>
                            ชื่อสินค้า
                          </th>
                          <th className="text-start" style={{ width: 160 }}>
                            ราคาต่อชิ้น (บาท)
                          </th>
                          <th className="text-start" style={{ width: 140 }}>
                            น้ำหนักต่อชิ้น (กิโลกรัม)
                          </th>
                          <th className="text-end" style={{ width: 160 }}>
                            การจัดการ
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-muted py-4"
                            >
                              กำลังโหลด...
                            </td>
                          </tr>
                        ) : displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-muted py-4"
                            >
                              ยังไม่มีข้อมูล
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((it, i) => (
                            <tr key={getId(it, i)}>
                              <td className="text-start">
                                {it.pdCode ?? it.code}
                              </td>
                              <td className="text-start fw-600">
                                {it.pdName ?? it.name}
                              </td>
                              <td className="text-start">
                                {Number(it.pdPrice ?? it.price ?? 0)}
                              </td>
                              <td className="text-start">
                                {Number(it.pdWeight ?? it.weight ?? 0).toFixed(
                                  2,
                                )}
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button
                                    size="sm"
                                    color="warning"
                                    onClick={() => openEditModal(it)}
                                  >
                                    แก้ไข
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="danger"
                                    onClick={() => deleteProduct(it)}
                                  >
                                    ลบ
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Summary */}
                <div className="d-flex justify-content-end justify-content-sm-end justify-content-center mt-2">
                  <div className="text-muted small">
                    แสดง {(page - 1) * pageSize + (displayRows.length ? 1 : 0)}–
                    {Math.min(page * pageSize, filteredTotal)} จากทั้งหมด{" "}
                    {filteredTotal} รายการ
                  </div>
                </div>

                {/* ✅ CSS desktop/tablet table */}
                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{ margin:0; }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{ background:#f9fbff; }
                  .fw-600{ font-weight:600; }
                `}</style>
              </CardBody>

              {/* Pagination */}
              {filteredTotal > 0 && (
                <CardFooter
                  className="py-4"
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <div className="paging-wrap">
                    <button
                      className="paging-btn icon"
                      onClick={() => setPage(TABLE_KEY, Math.max(1, page - 1))}
                      disabled={page === 1}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>

                    {pages.map((p) => (
                      <button
                        key={p}
                        className={`paging-btn ${page === p ? "active" : ""}`}
                        onClick={() => setPage(TABLE_KEY, p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(
                          TABLE_KEY,
                          Math.min(filteredPageCount, page + 1),
                        )
                      }
                      disabled={page === filteredPageCount}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal: เพิ่ม/แก้ไข */}
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={toggle}>
          {mode === "add" ? "เพิ่มสินค้า" : "แก้ไขสินค้า"}
        </ModalHeader>
        <Form onSubmit={onSubmit}>
          <ModalBody>
            {/* ✅ edit ต้องมี code */}
            {mode === "edit" && (
              <FormGroup>
                <Label htmlFor="prod-code">รหัสสินค้า</Label>
                <Input
                  id="prod-code"
                  name="code"
                  value={form.code}
                  onChange={onChange}
                  required
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="prod-name">ชื่อสินค้า</Label>
              <Input
                id="prod-name"
                name="name"
                value={form.name}
                onChange={onChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="prod-price">ราคาต่อชิ้น (บาท)</Label>
              <Input
                id="prod-price"
                name="price"
                type="number"
                min="0"
                step="1"
                value={form.price}
                onChange={onChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="prod-weight">น้ำหนักต่อชิ้น (กิโลกรัม)</Label>
              <Input
                id="prod-weight"
                name="weight"
                type="number"
                min="0"
                step="0.001"
                value={form.weight}
                onChange={onChange}
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              ยกเลิก
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              style={{
                height: 48,
                fontWeight: 700,
                backgroundColor: "#1171ef",
                color: "#fff",
                border: "none",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting
                ? mode === "edit"
                  ? "กำลังแก้ไข..."
                  : "กำลังบันทึก..."
                : mode === "edit"
                  ? "อัปเดต"
                  : "บันทึก"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default ProductCustomer;
